import React, { useState, useEffect, useRef, useMemo, memo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import videoData from './SearchVideo.json';
import "./Search.css";

const SearchPage = memo(() => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState(state?.searchTerm || "");
  const [suggestions, setSuggestions] = useState([]);
  const [searchHistory, setSearchHistory] = useState(() => {
    const savedHistory = localStorage.getItem("searchHistory");
    return savedHistory ? JSON.parse(savedHistory) : [];
  });
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Get selected language from localStorage, default to "english"
  const selectedLanguage = localStorage.getItem("userLanguage")?.toLowerCase() || "english";

  // Compute suggestions based on available videos in the selected language
  const allSuggestions = useMemo(() => {
    if (!videoData.videos) {
      console.error("videoData.videos is undefined");
      return [];
    }
    const videoKeys = Object.keys(videoData.videos).filter(key =>
      videoData.videos[key].some(video => 
        selectedLanguage === "both" || video.language.toLowerCase() === selectedLanguage
      )
    );
    const filteredSuggestions = videoData.suggestions.filter(suggestion =>
      videoKeys.some(key => key.toLowerCase() === suggestion.toLowerCase())
    );
    console.log("All Suggestions:", filteredSuggestions);
    return filteredSuggestions;
  }, [selectedLanguage]);

  // Debounced suggestion filtering
  useEffect(() => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      if (searchTerm.trim()) {
        const filteredSuggestions = allSuggestions.filter(suggestion =>
          suggestion.toLowerCase().includes(searchTerm.trim().toLowerCase())
        );
        console.log("Search Term:", searchTerm, "Filtered Suggestions:", filteredSuggestions);
        setSuggestions(filteredSuggestions);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 200);

    return () => clearTimeout(typingTimeoutRef.current);
  }, [searchTerm, allSuggestions]);

  // Auto-focus input on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Listen for language changes in localStorage
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "userLanguage") {
        setSearchTerm("");
        setShowSuggestions(false);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const handleSuggestionClick = (suggestion) => {
    const normalizedSuggestion = suggestion.toLowerCase();
    if (!searchHistory.includes(suggestion)) {
      const updatedHistory = [suggestion, ...searchHistory].slice(0, 5);
      setSearchHistory(updatedHistory);
      localStorage.setItem("searchHistory", JSON.stringify(updatedHistory));
    }
    setShowSuggestions(false);
    console.log("Navigating with:", { searchTerm: normalizedSuggestion, language: selectedLanguage });
    navigate(`/page-video`, { state: { searchTerm: normalizedSuggestion, language: selectedLanguage } });
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchSubmit = () => {
    if (searchTerm.trim()) {
      const normalizedSearch = searchTerm.trim();
      if (!searchHistory.includes(normalizedSearch)) {
        const updatedHistory = [normalizedSearch, ...searchHistory].slice(0, 5);
        setSearchHistory(updatedHistory);
        localStorage.setItem("searchHistory", JSON.stringify(updatedHistory));
      }
      setShowSuggestions(false);
      console.log("Navigating with:", { searchTerm: normalizedSearch.toLowerCase(), language: selectedLanguage });
      navigate(`/page-video`, { state: { searchTerm: normalizedSearch.toLowerCase(), language: selectedLanguage } });
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearchSubmit();
    }
  };

  const handleHistoryClick = (historyItem) => {
    const normalizedHistoryItem = historyItem.toLowerCase();
    if (!searchHistory.includes(historyItem)) {
      const updatedHistory = [historyItem, ...searchHistory].slice(0, 5);
      setSearchHistory(updatedHistory);
      localStorage.setItem("searchHistory", JSON.stringify(updatedHistory));
    }
    setShowSuggestions(false);
    navigate(`/page-video`, { state: { searchTerm: normalizedHistoryItem, language: selectedLanguage } });
  };

  const handleRemoveHistoryItem = (e, historyItem) => {
    e.stopPropagation(); // Prevent click from triggering handleHistoryClick
    const updatedHistory = searchHistory.filter(item => item !== historyItem);
    setSearchHistory(updatedHistory);
    localStorage.setItem("searchHistory", JSON.stringify(updatedHistory));
  };

  const highlightText = (text, highlight) => {
    if (!highlight.trim()) return text;
    const parts = text.split(new RegExp(`(${highlight.trim()})`, "gi"));
    return parts.map((part, index) =>
      part.toLowerCase() === highlight.trim().toLowerCase() ? (
        <span key={index} className="highlight">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const shouldShowHistory = () => {
    if (!searchTerm.trim()) return true;
    return searchHistory.some(historyItem =>
      historyItem.toLowerCase().includes(searchTerm.trim().toLowerCase())
    );
  };

  return (
    <div className="search-page">
      <div className="search-container">
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search Smarter"
          onKeyPress={handleKeyPress}
          className="search-input"
        />
        <button className="search-button" onClick={handleSearchSubmit}>
          <span className="search-icon">🔍</span>
        </button>
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <ul className="suggestion-list">
          {suggestions.map((suggestion, index) => (
            <li
              key={index}
              className="suggestion-item"
              onClick={() => handleSuggestionClick(suggestion)}
            >
              {highlightText(suggestion, searchTerm)}
            </li>
          ))}
        </ul>
      )}

      {searchHistory.length > 0 && shouldShowHistory() && (
        <ul className="history-list">
          {searchHistory.map((historyItem, index) => (
            <li
              key={index}
              className="history-item"
              onClick={() => handleHistoryClick(historyItem)}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => e.key === "Enter" && handleHistoryClick(historyItem)}
            >
              <span className="history-item-text">
                <span className="history-icon">🕒</span>
                {historyItem}
              </span>
              <button
                className="remove-history-button"
                onClick={(e) => handleRemoveHistoryItem(e, historyItem)}
                aria-label={`Remove ${historyItem} from history`}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});

export default SearchPage;